import { TypedDocumentNode, gql } from '@apollo/client';
import { faCardsBlank } from '@fortawesome/pro-regular-svg-icons';
import {
  faArrowCircleDown,
  faArrowCircleUp,
  faBullseyeArrow,
  faDoorOpen,
  faHandsHelping,
  faHourglassStart,
} from '@fortawesome/pro-solid-svg-icons';
import { defineMessages } from 'react-intl';
import styled from 'styled-components';

import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { attributes } from '@sorare/core/src/lib/players';

import InfoLineWithLogoAndValue from 'pages/players/$slug/Overview/InfoLineWithLogoAndValue';

import {
  SeasonStatsQuery,
  SeasonStatsQueryVariables,
} from './__generated__/index.graphql';

type Props = {
  playerSlug: string;
  seasonStartYear: number;
};

const messages = defineMessages({
  matchPlayed: {
    id: 'Player.SeasonStats.matchPlayed',
    defaultMessage: 'Match played',
  },
});

export const SEASON_STATS_QUERY = gql`
  query SeasonStatsQuery($playerSlug: String!, $seasonStartYear: Int!) {
    football {
      player(slug: $playerSlug) {
        slug
        stats(seasonStartYear: $seasonStartYear) {
          id
          assists
          appearances
          goals
          yellowCards
          redCards
          minutesPlayed
          substituteIn
          substituteOut
        }
      }
    }
  }
` as TypedDocumentNode<SeasonStatsQuery, SeasonStatsQueryVariables>;

const emptyValues = {
  appearances: '-',
  assists: '-',
  goals: '-',
  minutesPlayed: '-',
  redCards: '-',
  substituteIn: '-',
  substituteOut: '-',
  yellowCards: '-',
};

const StyledVertical = styled(Vertical).attrs({ gap: 1.5 })`
  border-radius: var(--double-unit);
`;

const SeasonStats = ({ playerSlug, seasonStartYear }: Props) => {
  const { data } = useQuery(SEASON_STATS_QUERY, {
    variables: {
      playerSlug,
      seasonStartYear,
    },
  });

  const {
    appearances,
    assists,
    goals,
    minutesPlayed,
    redCards,
    substituteIn,
    substituteOut,
    yellowCards,
  } = data?.football.player.stats || emptyValues;

  const renderIcon = (icon: FontAwesomeIconProps['icon']) => (
    <FontAwesomeIcon icon={icon} />
  );
  return (
    <StyledVertical>
      <InfoLineWithLogoAndValue
        message={messages.matchPlayed}
        value={appearances}
        icon={renderIcon(faDoorOpen)}
      />
      <InfoLineWithLogoAndValue
        message={attributes.minPlayed}
        value={minutesPlayed}
        icon={renderIcon(faHourglassStart)}
      />
      <InfoLineWithLogoAndValue
        message={attributes.assists}
        value={assists}
        icon={renderIcon(faHandsHelping)}
      />
      <InfoLineWithLogoAndValue
        message={attributes.goals}
        value={goals}
        icon={renderIcon(faBullseyeArrow)}
      />
      <InfoLineWithLogoAndValue
        iconColor="green"
        message={attributes.substituteIn}
        value={substituteIn}
        icon={renderIcon(faArrowCircleUp)}
      />
      <InfoLineWithLogoAndValue
        iconColor="red"
        message={attributes.substituteOut}
        value={substituteOut}
        icon={renderIcon(faArrowCircleDown)}
      />
      <InfoLineWithLogoAndValue
        iconColor="yellow"
        message={attributes.yellowCards}
        value={yellowCards}
        icon={renderIcon(faCardsBlank)}
      />
      <InfoLineWithLogoAndValue
        iconColor="red"
        message={attributes.redCards}
        value={redCards}
        icon={renderIcon(faCardsBlank)}
      />
    </StyledVertical>
  );
};

export default SeasonStats;
